import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { dmy, parseDmy, MONTHS } from "@rental/shared";
import { fileUrl } from "./lib/api.js";
import { C, inputStyle } from "./theme.js";

/* ============================================================================
   שני הרכיבים שמעורבים בצילום ובהצגה של קבצים.

   הם ישבו בכותרת של קובץ הארטיפקט. StoredImage השתנה מהותית — הוא כבר לא
   קורא data URL מ-window.storage אלא מצביע על נתיב בשרת, שמפנה לקישור חתום
   קצר-מועד. המשמעות: התמונות עצמן לא נמצאות בדפדפן ולא נשמרות בו, וגישה
   אליהן נבדקת מחדש בכל בקשה.
   ============================================================================ */

/* בחלק מהדפדפנים בתוך מסגרת מוטמעת, input שמוסתר ב-display:none לא נפתח
   בלחיצה על label. לכן הקלט עצמו נפרס שקוף מעל הכפתור והמשתמש לוחץ עליו. */
export function FilePick({ accept = "image/*", capture, onPick, disabled, title, children, style, className }) {
  return (
    <span className={`relative inline-flex items-center justify-center ${className || ""}`}
      style={{ ...style, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}
      title={title}>
      {children}
      <input
        type="file" accept={accept} {...(capture ? { capture: "environment" } : {})}
        disabled={disabled} aria-label={title}
        onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) onPick(f); }}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
      />
    </span>
  );
}

/**
 * תמונה שנשמרה בשרת.
 *
 * @param scope "owner" או "tenant" — קובע דרך איזה שער הקובץ נקרא. לדייר יש
 *              שער נפרד שבודק שהקובץ באמת שייך לדירה שלו.
 */
/* ============================================================================
   כפתור "אחורה" של הטלפון.

   האפליקציה היא דף אחד, ולכן "אחורה" במכשיר יצא מהאתר — גם כשהיה פתוח
   חלון או מסך פנימי. הפתרון: כל שכבה (חלון, מסך מלא, פאנל העוזר) דוחפת
   רשומה להיסטוריית הדפדפן כשהיא נפתחת. "אחורה" שולף את הרשומה → popstate
   → השכבה נסגרת. סגירה מהממשק (X) שולפת את הרשומה שלה בעצמה, כדי
   שההיסטוריה לא תתמלא ברשומות מתות.

   הרשומות נערמות ב-state.layers, ולכן שכבה יודעת אם היא עדיין בתוקף גם
   כשיש שכבות מעליה (אישור מעל חלון): היא נסגרת רק כשהאסימון שלה נעלם.
   ============================================================================ */
/**
 * @param enabled ברירת המחדל true — שכבה עצמאית שדוחפת בעליה ושולפת
 *   בסגירה. false משמש למודל שהוא "סעיף" בתוך מסך עם כמה סעיפים
 *   מתחלפים (לשוניות; ר' Modal ב-App.jsx), שם השכבה האחת המשותפת
 *   נוהלת ברמת המסך כולו ולא בכל סעיף בנפרד — ראו את ההערה בהמשך
 *   על הבעיה שזה פותר.
 */
export function useBackLayer(onBack, enabled = true) {
  const cb = useRef(onBack);
  cb.current = onBack;
  /* האסימון והדגל חיים ב-ref ולא בתוך האפקט: בפיתוח StrictMode מריץ
     כל אפקט פעמיים (mount → cleanup → mount). בגרסה הקודמת ה-cleanup
     המדומה קרא ל-history.back(), ההרצה השנייה דחפה אסימון חדש, ואז
     ה-popstate של ה-back המאוחר לא מצא אותו — וכל חלון נסגר ברגע
     שנפתח. כאן ההרצה השנייה מזהה שהאסימון עדיין בהיסטוריה, וה-back
     נדחה בטיק אחד כדי לוודא שזו באמת סגירה ולא הרצה כפולה. */
  const tokenRef = useRef(null);
  const alive = useRef(false);

  useEffect(() => {
    /* enabled=false: לא דוחפים ולא מאזינים כלל. משמש כשמודל הוא סעיף
       בתוך מסך רב-סעיפים (יש לו tabs) — מעבר בין סעיפים מבטל את
       הרכיב הישן ומרכיב את החדש באותו commit, לפני שה-cleanup הישן
       (שמתעכב בטיק) הספיק לשלוף את האסימון שלו. כל סעיף כזה היה דוחף
       אסימון חדש מעל הישן, שנשאר תקוע בהיסטוריה לצמיתות — ומעבר בין
       כמה סעיפים צובר כמה רשומות מתות כאלה. ה-back של הטלפון היה צריך
       כמה לחיצות "מתות" לפני שקורה משהו נראה לעין. הפתרון: הסעיפים
       עצמם לא מנהלים שכבה בכלל, והמסך שמכיל אותם (הכרטיס המאוחד /
       TenantHistory) מנהל שכבה אחת יחידה למשך כל זמן שהוא פתוח,
       בלי קשר לאיזה סעיף מוצג. */
    if (!enabled) return undefined;
    if (typeof window === "undefined" || !window.history?.pushState) return undefined;
    alive.current = true;
    const token = tokenRef.current || (tokenRef.current = Math.random().toString(36).slice(2, 10));
    const base = window.history.state || {};
    if (!(base.layers || []).includes(token)) {
      window.history.pushState({ ...base, layers: [...(base.layers || []), token] }, "");
    }

    const onPop = () => {
      const layers = window.history.state?.layers || [];
      if (!layers.includes(token)) cb.current?.();
    };
    window.addEventListener("popstate", onPop);

    return () => {
      alive.current = false;
      window.removeEventListener("popstate", onPop);
      setTimeout(() => {
        if (alive.current) return;   // הרצה כפולה של StrictMode — השכבה עדיין פתוחה
        const layers = window.history.state?.layers || [];
        // נסגר מהממשק בזמן שהוא השכבה העליונה — מנקה את הרשומה שלו.
        if (layers.length && layers[layers.length - 1] === token) window.history.back();
      }, 0);
    };
    // enabled ב-deps: כרטיס הנכס המאוחד קורא ל-useBackLayer תמיד (לא
    // בתנאי — כללי ה-hooks), עם enabled שעוקב אחרי אם הכרטיס פתוח.
    // המעבר false→true/true→false צריך להריץ מחדש את האפקט כדי לדחוף/
    // לשלוף בדיוק בפתיחה/סגירה של הכרטיס כולו, לא של סעיף בתוכו.
  }, [enabled]);
}

/** אותו דבר כרכיב, למקומות שבהם נוח יותר לשים אלמנט מאשר hook. */
export function BackLayer({ onBack }) {
  useBackLayer(onBack);
  return null;
}

/** חלון מודאלי גנרי — כותרת, X לסגירה, ולחיצה על הרקע. */
export function Modal({ title, children, onClose, wide, tabs = null }) {
  /* tabs מגיע רק כשהמודל הזה הוא "סעיף" בתוך מסך רב-סעיפים (הכרטיס
     המאוחד / TenantHistory) — שם המסך המכיל מנהל שכבת back אחת
     ומשותפת לכל משך ההצגה שלו, כדי שמעבר בין סעיפים (unmount+mount
     של מודלים שונים באותו commit) לא ישאיר רשומות מתות בהיסטוריית
     הדפדפן. ר' התיעוד המלא ב-useBackLayer. מודל בלי tabs הוא עצמאי
     לגמרי וממשיך לנהל שכבה משלו כרגיל. */
  useBackLayer(onClose, !tabs);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" style={{ background: "rgba(20,38,43,0.45)" }} onClick={onClose}>
      <div dir="rtl" className={`w-full ${wide ? "sm:max-w-2xl" : "sm:max-w-md"} rounded-t-2xl sm:rounded-2xl p-5 max-h-full overflow-y-auto`}
        /* צבע מפורש, לא רק רקע: מודל שנפתח מכפתור בתוך הכותרת הכהה
           (.em-appbar, color:#fff) ירש טקסט לבן בלי זה — בלתי נראה על
           הרקע הבהיר של המודל, בדיוק כמו placeholder על placeholder. */
        style={{ background: C.card, color: C.ink }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="disp text-lg font-bold">{title}</h3>
          <button onClick={onClose} aria-label="סגירה" style={{ color: C.muted }}><X size={18} /></button>
        </div>
        {tabs && <div className="flex items-center gap-1.5 mb-4 -mt-1 flex-wrap">{tabs}</div>}
        {children}
      </div>
    </div>
  );
}

export function StoredImage({ id, alt = "", className, style, scope = "owner", muted = "#EEF1EE", eager = false }) {
  const [failed, setFailed] = useState(false);
  const src = fileUrl(id, scope);

  if (!src) return <div className={className} style={{ ...style, background: muted }} />;

  // כשל טעינה = קובץ שאינו תמונה (חוזה סרוק ב-PDF) או הרשאה שפגה. בשני
  // המקרים קישור לפתיחה בלשונית חדשה עדיף על ריבוע שבור.
  if (failed) {
    return (
      <a href={src} target="_blank" rel="noreferrer" className={className}
        style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", background: muted, fontSize: 12 }}>
        פתיחת הקובץ
      </a>
    );
  }

  return (
    <img src={src} alt={alt || ""} className={className} style={style}
      /* eager — לחתימות במסמך שמודפס: תמונה עצלה שלא נגללה אליה לא
         נטענת עד ההדפסה, והחתימה יוצאת ריקה ב-PDF. */
      loading={eager ? "eager" : "lazy"} onError={() => setFailed(true)} />
  );
}

/* ============================================================================
   שדה תאריך.

   <input type="date"> מציג את התאריך לפי הגדרת השפה של הדפדפן ולא לפי
   השפה של הדף — דפדפן שמוגדר אנגלית מציג 08/16/2026 גם ב-lang="he", ואין
   שום תכונה שמשנה את זה. המשתמש רואה חודש/יום/שנה וטועה. חוץ מזה, הבורר
   של המערכת הוא ממשק דפדפן/מערכת הפעלה שאי אפשר לעצב — ואין בו דרך
   מהירה לקפוץ לשנה רחוקה.

   לכן התצוגה כאן היא שדה טקסט משלנו בסדר קבוע — יום, חודש, שנה משמאל
   לימין — והכפתור שלצידו פותח לוח שנה שבנינו בעצמנו, עם קומבו שנה
   בכותרת. הערך שנשמר הוא תמיד ISO (YYYY-MM-DD), בדיוק כמו קודם, ולכן שום
   חישוב לא משתנה.
   ============================================================================ */

const WEEKDAYS_HE = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];
const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/* טווח השנים בקומבו: מאה שנה אחורה (מסמכים ישנים) עד עשר קדימה (חוזים
   ארוכי טווח), מהחדש לישן כי רוב הבחירות קרובות להווה. */
const CAL_YEAR_NOW = new Date().getFullYear();
const CAL_YEAR_OPTIONS = Array.from({ length: 111 }, (_, i) => CAL_YEAR_NOW + 10 - i);

/* 42 תאים (6 שבועות) עבור חודש נתון, כולל ימי "גלישה" מהחודשים הסמוכים —
   new Date עם יום מחוץ לטווח מתגלגל אוטומטית לחודש הקודם/הבא. */
function monthGrid(y, m) {
  const first = new Date(y, m - 1, 1);
  const startOffset = first.getDay();
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(y, m - 1, i - startOffset + 1);
    cells.push({ iso: isoOf(d), day: d.getDate(), inMonth: d.getMonth() === m - 1 });
  }
  return cells;
}

/* לוח השנה עצמו — נפתח ב-portal לגוף המסמך כדי לא להיחתך בתוך טפסים
   גוללים בתוך מודלים, וממוקם fixed לפי מיקום הכפתור שפתח אותו. */
function CalendarPopover({ value, anchorRef, onPick, onClose }) {
  const today = new Date();
  const [viewY, setViewY] = useState(() => (value ? Number(value.slice(0, 4)) : today.getFullYear()));
  const [viewM, setViewM] = useState(() => (value ? Number(value.slice(5, 7)) : today.getMonth() + 1));
  const [pos, setPos] = useState(null);
  const popRef = useRef(null);

  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 264, height = 300;
    const openUp = window.innerHeight - r.bottom < height + 8 && r.top > height + 8;
    const left = Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8);
    setPos({ left, top: openUp ? r.top - height - 6 : r.bottom + 6 });
  }, [anchorRef]);

  useEffect(() => {
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [anchorRef, onClose]);

  const cells = useMemo(() => monthGrid(viewY, viewM), [viewY, viewM]);
  const todayIso = isoOf(today);

  const goPrev = () => { if (viewM === 1) { setViewM(12); setViewY((y) => y - 1); } else setViewM((m) => m - 1); };
  const goNext = () => { if (viewM === 12) { setViewM(1); setViewY((y) => y + 1); } else setViewM((m) => m + 1); };

  if (!pos) return null;

  return createPortal(
    <div ref={popRef} role="dialog" aria-label="בחירת תאריך"
      style={{
        position: "fixed", top: pos.top, left: pos.left, width: 264, zIndex: 200,
        background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
        boxShadow: "0 8px 24px rgba(18,33,29,.18)", padding: 10,
      }}>
      <div className="flex items-center justify-between gap-1" style={{ marginBottom: 8 }}>
        <button type="button" onClick={goPrev} aria-label="חודש קודם"
          className="w-7 h-7 rounded-md flex items-center justify-center" style={{ color: C.teal }}>
          <ChevronRight size={16} />
        </button>
        <div className="flex items-center gap-1" style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>
          <span>{MONTHS[viewM - 1]}</span>
          <select value={viewY} onChange={(e) => setViewY(Number(e.target.value))} aria-label="שנה"
            style={{ border: `1px solid ${C.line}`, borderRadius: 8, padding: "2px 4px", fontSize: 13, background: "#FBFDFC", color: C.ink }}>
            {CAL_YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button type="button" onClick={goNext} aria-label="חודש הבא"
          className="w-7 h-7 rounded-md flex items-center justify-center" style={{ color: C.teal }}>
          <ChevronLeft size={16} />
        </button>
      </div>
      <div className="grid" style={{ gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center" }}>
        {WEEKDAYS_HE.map((w) => <div key={w} style={{ fontSize: 11, color: C.muted, padding: "2px 0" }}>{w}</div>)}
        {cells.map((c) => {
          const selected = c.iso === value;
          const isToday = c.iso === todayIso;
          return (
            <button key={c.iso} type="button" onClick={() => onPick(c.iso)}
              style={{
                fontSize: 12.5, padding: "6px 0", borderRadius: 8, margin: 1,
                color: !c.inMonth ? C.muted : selected ? "#fff" : C.ink,
                background: selected ? C.teal : "transparent",
                opacity: !c.inMonth ? 0.45 : 1,
                border: isToday && !selected ? `1px solid ${C.teal}` : "1px solid transparent",
              }}>
              {c.day}
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

export function DateField({ value, onChange, style, ariaLabel, onEnter }) {
  const [text, setText] = useState(() => dmy(value));
  const btn = useRef(null);
  const typing = useRef(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  /* ערך שהשתנה מבחוץ (בחירה מהירה באשף, טופס שנטען) חייב להשתקף — אבל
     לא בזמן שהמשתמש באמצע הקלדה, אחרת הסמן קופץ בכל תו. */
  useEffect(() => {
    if (!typing.current) setText(dmy(value));
  }, [value]);

  const commit = (raw) => {
    const iso = parseDmy(raw);
    if (iso) onChange(iso);
    else if (!raw.trim()) onChange("");
  };

  const onType = (e) => {
    const raw = e.target.value;
    // השלמת הלוכסנים תוך כדי הקלדה, בלי להפריע למחיקה.
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    const auto = raw.endsWith("/") || raw.length < text.length
      ? raw
      : [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join("/");

    typing.current = true;
    setText(auto);
    commit(auto);
  };

  const invalid = text.trim() !== "" && !parseDmy(text);

  return (
    <div className="flex gap-1.5" style={{ position: "relative" }}>
      <input
        type="text"
        inputMode="numeric"
        dir="ltr"
        aria-label={ariaLabel}
        placeholder="יום/חודש/שנה"
        value={text}
        onChange={onType}
        onBlur={() => { typing.current = false; setText(dmy(value)); }}
        onKeyDown={(e) => { if (e.key === "Enter" && onEnter) onEnter(); }}
        style={{
          ...inputStyle,
          textAlign: "left",
          ...(invalid ? { borderColor: "#C08A2E" } : {}),
          ...style,
        }}
      />
      <button
        ref={btn}
        type="button"
        onClick={() => setPickerOpen((o) => !o)}
        aria-label="בחירה מלוח שנה"
        aria-expanded={pickerOpen}
        className="rounded-lg px-2.5 flex items-center shrink-0"
        style={{ border: `1px solid ${C.line}`, color: C.teal, background: "#fff" }}
      >
        <CalendarDays size={15} />
      </button>

      {pickerOpen && (
        <CalendarPopover
          value={value}
          anchorRef={btn}
          onPick={(iso) => { typing.current = false; onChange(iso); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
